/**
 * Rules tab for a CRM contact — per-contact Zeus rules.
 *
 * These rules OVERRIDE the user's general Zeus guardrails when both apply.
 * Two ways to build a rule:
 * - Schedule rule: simple structured builder (days, time window, condition)
 * - Flex rule: a short AI interview asks questions, then proposes the rule
 *   for the user to save.
 */

import { useRef, useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarClock, Sparkles, Trash2, Loader2, Plus, Send, ShieldCheck } from "lucide-react";

interface ContactZeusRule {
  id: string;
  name: string;
  ruleKind: "schedule" | "flex";
  allowedDays: string[] | null;
  startTime: string | null;
  endTime: string | null;
  conditions: string | null;
  ruleText: string;
  isActive: boolean;
}

interface ProposedRule {
  name: string;
  ruleKind: "flex";
  ruleText: string;
  allowedDays?: string[] | null;
  startTime?: string | null;
  endTime?: string | null;
  conditions?: string | null;
}

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function compileRulePreview(days: string[], start: string, end: string, conditions: string): string {
  const parts: string[] = [];
  if (days.length) parts.push(`Only schedule on: ${days.join(", ")}.`);
  if (start && end) parts.push(`Appointments only between ${start} and ${end}.`);
  else if (start) parts.push(`Appointments only at or after ${start}.`);
  else if (end) parts.push(`Appointments must end by ${end}.`);
  if (conditions.trim()) parts.push(`Condition: ${conditions.trim()}`);
  return parts.join(" ");
}

export function ContactRulesTab({ contactId }: { contactId: string }) {
  const { toast } = useToast();
  const rulesKey = [`/api/contacts/${contactId}/zeus-rules`];

  const { data, isLoading } = useQuery<{ rules: ContactZeusRule[] }>({
    queryKey: rulesKey,
    queryFn: getQueryFn({ on401: "throw" }),
  });

  // Builder state
  const [builderKind, setBuilderKind] = useState<"schedule" | "flex">("schedule");
  const [name, setName] = useState("");
  const [days, setDays] = useState<string[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [conditions, setConditions] = useState("");

  // Flex chat state
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [proposal, setProposal] = useState<ProposedRule | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: rulesKey });

  const resetBuilder = () => {
    setName("");
    setDays([]);
    setStartTime("");
    setEndTime("");
    setConditions("");
    setChat([]);
    setChatInput("");
    setProposal(null);
  };

  const createRule = useMutation({
    mutationFn: (rule: Record<string, unknown>) =>
      apiRequest("POST", `/api/contacts/${contactId}/zeus-rules`, rule),
    onSuccess: () => {
      resetBuilder();
      refresh();
      toast({ title: "Rule saved", description: "Zeus will follow this rule for this contact." });
    },
    onError: (err: any) =>
      toast({ title: "Could not save rule", description: err?.message ?? "Try again.", variant: "destructive" }),
  });

  const toggleRule = useMutation({
    mutationFn: (vars: { ruleId: string; isActive: boolean }) =>
      apiRequest("PATCH", `/api/contacts/${contactId}/zeus-rules/${vars.ruleId}`, { isActive: vars.isActive }),
    onSuccess: refresh,
  });

  const deleteRule = useMutation({
    mutationFn: (ruleId: string) =>
      apiRequest("DELETE", `/api/contacts/${contactId}/zeus-rules/${ruleId}`),
    onSuccess: refresh,
  });

  const flexChat = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", `/api/contacts/${contactId}/zeus-rules/flex-chat`, {
        message,
        conversationHistory: chat,
      });
      return (await res.json()) as { reply: string; proposedRule: ProposedRule | null };
    },
    onSuccess: (result, message) => {
      setChat((c) => [...c, { role: "user", content: message }, { role: "assistant", content: result.reply || "Here's the rule I put together:" }]);
      if (result.proposedRule) setProposal(result.proposedRule);
    },
    onError: (err: any) =>
      toast({ title: "Rule assistant unavailable", description: err?.message ?? "Try again.", variant: "destructive" }),
  });

  const sendChat = () => {
    const msg = chatInput.trim();
    if (!msg || flexChat.isPending) return;
    setChatInput("");
    flexChat.mutate(msg);
  };

  const saveScheduleRule = () => {
    const ruleText = compileRulePreview(days, startTime, endTime, conditions);
    createRule.mutate({
      name: name.trim(),
      ruleKind: "schedule",
      allowedDays: days.length ? days : null,
      startTime: startTime || null,
      endTime: endTime || null,
      conditions: conditions.trim() || null,
      ruleText,
      isActive: true,
    });
  };

  const saveFlexRule = () => {
    if (!proposal) return;
    createRule.mutate({
      name: proposal.name,
      ruleKind: "flex",
      allowedDays: proposal.allowedDays ?? null,
      startTime: proposal.startTime ?? null,
      endTime: proposal.endTime ?? null,
      conditions: proposal.conditions ?? null,
      ruleText: proposal.ruleText,
      isActive: true,
    });
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading rules…
      </div>
    );
  }

  const schedulePreview = compileRulePreview(days, startTime, endTime, conditions);
  const scheduleValid = name.trim().length > 0 && schedulePreview.length > 0;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4" /> Rules for this contact
        </h3>
        <p className="text-sm text-muted-foreground">
          These rules apply only to this contact and <span className="font-medium">override your general Zeus
          guardrails</span>. Example: your guardrail says dentists only on Tuesdays, but this contact's rule says
          Thursdays — Zeus follows this contact's rule.
        </p>
      </div>

      {/* ── Existing rules ─────────────────────────────────────────────── */}
      <div className="space-y-2">
        {data.rules.map((rule) => (
          <div key={rule.id} className="border rounded-md px-3 py-2 flex items-start justify-between gap-3" data-testid={`zeus-rule-${rule.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">{rule.name}</p>
                <Badge variant="secondary" className="text-xs">
                  {rule.ruleKind === "flex" ? "Flex" : "Schedule"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{rule.ruleText}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Switch
                checked={rule.isActive}
                onCheckedChange={(v) => toggleRule.mutate({ ruleId: rule.id, isActive: v })}
                data-testid={`switch-zeus-rule-${rule.id}`}
              />
              <Button variant="ghost" size="icon" onClick={() => deleteRule.mutate(rule.id)} data-testid={`button-delete-zeus-rule-${rule.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        {data.rules.length === 0 && (
          <p className="text-sm text-muted-foreground border rounded-md p-3">
            No rules yet — Zeus follows your general guardrails for this contact.
          </p>
        )}
      </div>

      {/* ── Rule builder ───────────────────────────────────────────────── */}
      <div className="border rounded-md p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">New rule</h4>
          <Select value={builderKind} onValueChange={(v) => setBuilderKind(v as "schedule" | "flex")}>
            <SelectTrigger className="w-[220px]" data-testid="select-rule-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="schedule">
                <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Schedule rule</span>
              </SelectItem>
              <SelectItem value="flex">
                <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Flex rule (AI helps)</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {builderKind === "schedule" ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="rule-name">Rule name</Label>
              <Input
                id="rule-name"
                placeholder="e.g. Appointment days"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="input-zeus-rule-name"
              />
            </div>

            <div>
              <Label>Allowed days</Label>
              <p className="text-xs text-muted-foreground mb-2">Tap the days Zeus may schedule with this contact. Leave all off for any day.</p>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={days.includes(d) ? "default" : "outline"}
                    onClick={() => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]))}
                    data-testid={`button-day-${d.toLowerCase()}`}
                  >
                    {d.slice(0, 3)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <Label htmlFor="rule-start">Earliest time</Label>
                <Input id="rule-start" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="input-zeus-rule-start" />
              </div>
              <div>
                <Label htmlFor="rule-end">Latest time</Label>
                <Input id="rule-end" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="input-zeus-rule-end" />
              </div>
            </div>

            <div>
              <Label htmlFor="rule-conditions">Condition (optional)</Label>
              <Textarea
                id="rule-conditions"
                rows={2}
                placeholder='e.g. "Only if it&#39;s a routine cleaning — anything urgent can be any day"'
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                data-testid="input-zeus-rule-conditions"
              />
            </div>

            {schedulePreview && (
              <div className="bg-muted rounded-md p-3 text-sm" data-testid="text-rule-preview">
                <p className="text-xs font-medium text-muted-foreground mb-1">Zeus will follow:</p>
                {schedulePreview}
              </div>
            )}

            <Button onClick={saveScheduleRule} disabled={!scheduleValid || createRule.isPending} data-testid="button-save-zeus-rule">
              {createRule.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Save rule
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Describe the rule in your own words — the assistant asks a couple of questions, then writes the rule for you.
            </p>

            <div className="border rounded-md p-3 max-h-64 overflow-y-auto space-y-2 bg-muted/30" data-testid="flex-chat-messages">
              {chat.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Try: "Only book afternoon appointments, and never on the weeks my kids are with me."
                </p>
              )}
              {chat.map((m, i) => (
                <div key={i} className={`text-sm rounded-md px-3 py-2 max-w-[85%] ${m.role === "user" ? "bg-primary text-primary-foreground ml-auto" : "bg-background border"}`}>
                  {m.content}
                </div>
              ))}
              {flexChat.isPending && (
                <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Describe the rule…"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                disabled={flexChat.isPending}
                data-testid="input-flex-chat"
              />
              <Button onClick={sendChat} disabled={!chatInput.trim() || flexChat.isPending} data-testid="button-send-flex-chat">
                <Send className="w-4 h-4" />
              </Button>
            </div>

            {proposal && (
              <div className="border rounded-md p-3 space-y-2 bg-muted/40" data-testid="flex-rule-proposal">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <p className="font-medium text-sm">{proposal.name}</p>
                </div>
                <p className="text-sm">{proposal.ruleText}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveFlexRule} disabled={createRule.isPending} data-testid="button-save-flex-rule">
                    {createRule.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Save this rule
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setProposal(null)} data-testid="button-discard-flex-rule">
                    Keep refining
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
